import { allByType, changes } from './store-adapter';
import type {
  Customer,
  DeviceType,
  HoseConfig,
  Inspection,
  Manufacturer,
  ServicePrice,
  Settings,
  SparePart,
  Visit,
  Worker,
} from '@/data/types';

/**
 * Initial state read from PouchDB on auth. Returns a partial slice the caller
 * can merge into the Zustand store (typically via useApp.setState(partial)).
 *
 * Reads each CouchDB doc type that the FE shadow-puts (apps/frontend/src/store/index.ts).
 * For singleton docs (settings, hose_config) returns the first match.
 * Empty result for a type leaves that slice undefined so the caller's existing
 * seed / localStorage state is preserved.
 *
 * Errors are swallowed so a first-run user (no PouchDB data yet) still boots
 * with seeded state.
 */
export interface InitPartial {
  customers?: Customer[];
  manufacturers?: Manufacturer[];
  deviceTypes?: DeviceType[];
  spareParts?: SparePart[];
  servicePrices?: ServicePrice[];
  visits?: Visit[];
  inspections?: Inspection[];
  workers?: Worker[];
  settings?: Settings;
  hoseConfig?: HoseConfig;
}

type AnyDoc = Record<string, unknown> & {
  _id?: string;
  _rev?: string;
  type: string;
  tenant_id?: string;
};

function stripMeta<T>(doc: AnyDoc): T {
  const { _id, _rev, type, tenant_id, ...rest } = doc;
  void _id;
  void _rev;
  void type;
  void tenant_id;
  return rest as T;
}

// Collapse forked docs (legacy-migration id-scheme mismatches) to one entry
// per domain key, keeping the most recently written doc. Docs without an
// updatedAt stamp sort oldest.
function dedupeBy(docs: AnyDoc[], keyFn: (d: AnyDoc) => unknown): AnyDoc[] {
  const byKey = new Map<unknown, AnyDoc>();
  for (const d of docs) {
    const key = keyFn(d);
    const prev = byKey.get(key);
    if (
      !prev ||
      ((d.updatedAt as number | undefined) ?? 0) >=
        ((prev.updatedAt as number | undefined) ?? 0)
    ) {
      byKey.set(key, d);
    }
  }
  return [...byKey.values()];
}

// The domain field SparePart.type (extinguisher|hose) collides with the
// CouchDB doc discriminator `type`, so it is persisted as `partType`
// (matching the legacy ETL). Restore it on hydration.
function toSparePart(doc: AnyDoc): SparePart {
  const part = stripMeta<SparePart & { partType?: string }>(doc);
  const { partType, ...rest } = part;
  return {
    ...rest,
    type: (partType as SparePart['type']) ?? 'extinguisher',
  } as SparePart;
}

export async function initStoreFromPouchDB(): Promise<InitPartial> {
  try {
    const [
      customerDocs,
      manufacturerDocs,
      deviceTypeDocs,
      sparePartDocs,
      servicePriceDocs,
      visitDocs,
      workerDocs,
      settingsDocs,
      hoseConfigDocs,
      inspectionDocs,
    ] = await Promise.all([
      allByType<AnyDoc>('customer'),
      allByType<AnyDoc>('manufacturer'),
      allByType<AnyDoc>('device_type'),
      allByType<AnyDoc>('spare_part'),
      allByType<AnyDoc>('service_price'),
      allByType<AnyDoc>('visit'),
      allByType<AnyDoc>('worker'),
      allByType<AnyDoc>('settings'),
      allByType<AnyDoc>('hose_config'),
      allByType<AnyDoc>('inspection'),
    ]);

    const out: InitPartial = {};
    if (customerDocs.length)
      out.customers = dedupeBy(customerDocs, d => d.id).map(
        stripMeta<Customer>
      );
    if (manufacturerDocs.length)
      out.manufacturers = manufacturerDocs.map(stripMeta<Manufacturer>);
    if (deviceTypeDocs.length)
      out.deviceTypes = deviceTypeDocs.map(stripMeta<DeviceType>);
    if (sparePartDocs.length)
      out.spareParts = dedupeBy(sparePartDocs, d => d.articleNr).map(
        toSparePart
      );
    if (servicePriceDocs.length)
      out.servicePrices = servicePriceDocs.map(stripMeta<ServicePrice>);
    if (visitDocs.length) out.visits = visitDocs.map(stripMeta<Visit>);
    if (inspectionDocs.length)
      out.inspections = inspectionDocs.map(stripMeta<Inspection>);
    if (workerDocs.length) out.workers = workerDocs.map(stripMeta<Worker>);
    if (settingsDocs.length)
      out.settings = stripMeta<Settings>(settingsDocs[0]);
    if (hoseConfigDocs.length)
      out.hoseConfig = stripMeta<HoseConfig>(hoseConfigDocs[0]);

    return out;
  } catch {
    return {};
  }
}

/**
 * Subscribe to PouchDB changes for catalog/entity doc types and feed
 * incoming docs into the supplied Zustand setter. Returns an unsubscribe
 * fn. The one-shot initStoreFromPouchDB call captures whatever has
 * replicated by then; this listener keeps the store live as the initial
 * pull arrives doc-by-doc and on subsequent sync events.
 */
const CATALOG_TYPES = [
  'customer',
  'manufacturer',
  'device_type',
  'spare_part',
  'service_price',
  'visit',
  'inspection',
  'worker',
  'settings',
  'hose_config',
];

export function subscribeStoreUpdates(
  apply: (partial: InitPartial) => void
): () => void {
  let pending: ReturnType<typeof setTimeout> | null = null;
  const refresh = () => {
    if (pending) clearTimeout(pending);
    // Debounce a burst of changes — re-read once after 150ms of quiet.
    pending = setTimeout(() => {
      pending = null;
      void initStoreFromPouchDB().then(partial => {
        if (Object.keys(partial).length) apply(partial);
      });
    }, 150);
  };
  return changes(CATALOG_TYPES, refresh);
}
